import useAfterMount from '@rio-cloud/rio-uikit/useAfterMount';
import useLocalStorage from '@rio-cloud/rio-uikit/useLocalStorage';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { useLoginInfo } from '#configuration/login/loginSlice.ts';
import {
    applyPersistedTableColumns,
    type PersistedTableColumn,
    reorderTableColumns,
    serializePersistedTableColumns,
} from '#utils/tableColumns.util.ts';

type PersistableTableColumn = {
    key: string;
    visible: boolean;
    width?: number | string;
};

type UsePersistedTableColumnsProps<T extends PersistableTableColumn> = {
    defaultColumns: T[];
    storageKeyPrefix: string;
};

const TABLE_COLUMNS_PERSIST_DEBOUNCE_TIME = 150;

const parseStoredColumns = <T extends string>(storageKey: string): PersistedTableColumn<T>[] | undefined => {
    const storedValue = window.localStorage.getItem(storageKey);

    if (!storedValue) {
        return undefined;
    }

    try {
        return JSON.parse(storedValue) as PersistedTableColumn<T>[];
    } catch (error) {
        console.error(`Failed to parse persisted table columns for key "${storageKey}"`, error);
        return undefined;
    }
};

export const usePersistedTableColumns = <T extends PersistableTableColumn>({
    defaultColumns,
    storageKeyPrefix,
}: UsePersistedTableColumnsProps<T>) => {
    const { accountId } = useLoginInfo();
    const accountPrefix = accountId?.slice(0, 6) ?? '';
    const storageKey = `${storageKeyPrefix}:${accountPrefix}`;
    const [storedColumns, setStoredColumns] = useLocalStorage<PersistedTableColumn<T['key']>[]>(storageKey, []);
    const [resetVersion, setResetVersion] = useState(0);
    const [columns, setColumns] = useState<T[]>(() => applyPersistedTableColumns(defaultColumns, storedColumns));

    useLayoutEffect(() => {
        const nextStoredColumns = parseStoredColumns<T['key']>(storageKey);
        setStoredColumns(nextStoredColumns);
        setColumns(applyPersistedTableColumns(defaultColumns, nextStoredColumns));
    }, [defaultColumns, setStoredColumns, storageKey]);

    const serializedDefaultColumns = useMemo(
        () => JSON.stringify(serializePersistedTableColumns(defaultColumns)),
        [defaultColumns]
    );

    const debouncedSyncStoredColumns = useMemo(
        () =>
            debounce((nextColumns: T[], nextSerializedDefaultColumns: string) => {
                const serializedColumns = serializePersistedTableColumns(nextColumns);

                if (JSON.stringify(serializedColumns) === nextSerializedDefaultColumns) {
                    setStoredColumns(undefined);
                    return;
                }

                setStoredColumns(serializedColumns);
            }, TABLE_COLUMNS_PERSIST_DEBOUNCE_TIME),
        [setStoredColumns]
    );

    useEffect(() => {
        return () => {
            debouncedSyncStoredColumns.cancel();
        };
    }, [debouncedSyncStoredColumns]);

    useAfterMount(() => {
        debouncedSyncStoredColumns(columns, serializedDefaultColumns);
    }, [columns, debouncedSyncStoredColumns, serializedDefaultColumns]);

    const updateColumns = useCallback((update: (currentColumns: T[]) => T[]) => {
        setColumns(currentColumns => update(currentColumns));
    }, []);

    const handleColumnOrderChange = useCallback(
        (columnOrder: string[]) => {
            updateColumns(currentColumns => reorderTableColumns(currentColumns, columnOrder));
        },
        [updateColumns]
    );

    const handleColumnVisibilityChange = useCallback(
        (columnKey: string, visible: boolean) => {
            updateColumns(currentColumns =>
                currentColumns.map(column => (column.key === columnKey ? { ...column, visible } : column))
            );
        },
        [updateColumns]
    );

    const handleColumnResize = useCallback(
        (columnKey: string, width?: number | string) => {
            updateColumns(currentColumns =>
                currentColumns.map(column => (column.key === columnKey ? { ...column, width } : column))
            );
        },
        [updateColumns]
    );

    const resetColumns = useCallback(() => {
        debouncedSyncStoredColumns.cancel();
        setStoredColumns(undefined);
        setColumns(defaultColumns);
        setResetVersion(currentResetVersion => currentResetVersion + 1);
    }, [debouncedSyncStoredColumns, defaultColumns, setStoredColumns]);

    return {
        columns,
        handleColumnOrderChange,
        handleColumnResize,
        handleColumnVisibilityChange,
        resetColumns,
        resetVersion,
    };
};
